import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  listAppSecrets,
  saveAppSecret,
  deleteAppSecret,
  getCronSchedule,
  updateCronSchedule,
  runScheduleNow,
} from "@/lib/admin.functions";

// ═══════════════════════════════════════════════════════════════
// API Keys tab
// ═══════════════════════════════════════════════════════════════

export function ApiKeysTab() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["admin-app-secrets"], queryFn: () => listAppSecrets() });
  const save = useMutation({
    mutationFn: (v: { name: string; value: string }) =>
      saveAppSecret({ data: v as any }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-app-secrets"] }),
  });
  const del = useMutation({
    mutationFn: (name: string) => deleteAppSecret({ data: { name } as any }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-app-secrets"] }),
  });

  return (
    <div className="space-y-4">
      <div className="card-glass">
        <h3 className="text-base font-semibold">Chaves de API</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Salve as chaves aqui para editá-las em runtime, sem redeploy. Valores em branco caem para
          a variável de ambiente correspondente (se houver). Nunca compartilhe estes valores.
        </p>
      </div>

      {list.isLoading && <p className="text-sm text-muted-foreground">Carregando...</p>}

      <div className="grid gap-4 md:grid-cols-2">
        {(list.data ?? []).map((item) => (
          <SecretCard
            key={item.name}
            item={item}
            onSave={(value) => save.mutateAsync({ name: item.name, value })}
            onDelete={() => del.mutateAsync(item.name)}
          />
        ))}
      </div>
    </div>
  );
}

function SecretCard({
  item,
  onSave,
  onDelete,
}: {
  item: {
    name: string;
    label: string;
    where: string;
    configured: boolean;
    source: "db" | "env" | "none";
    masked: string;
    updated_at: string | null;
  };
  onSave: (value: string) => Promise<any>;
  onDelete: () => Promise<any>;
}) {
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const badge = item.source === "db"
    ? { label: "Salva no banco", cls: "bg-primary/15 text-primary" }
    : item.source === "env"
      ? { label: "Fallback env", cls: "bg-amber-500/15 text-amber-600" }
      : { label: "Não configurada", cls: "bg-muted text-muted-foreground" };

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!value.trim()) return;
    setSaving(true);
    setMsg(null);
    try {
      await onSave(value.trim());
      setValue("");
      setMsg("Salvo.");
    } catch (err) {
      setMsg((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card-glass space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-semibold">{item.label}</p>
          <p className="mt-1 font-mono text-xs text-muted-foreground">{item.name}</p>
        </div>
        <span className={`rounded-full px-2 py-0.5 text-xs ${badge.cls}`}>{badge.label}</span>
      </div>
      <p className="text-xs text-muted-foreground">{item.where}</p>
      <div className="rounded-lg bg-secondary/40 px-3 py-2 font-mono text-xs">
        {item.configured ? item.masked : "— nenhuma chave —"}
      </div>
      {item.updated_at && (
        <p className="text-[11px] text-muted-foreground">
          Atualizada em {new Date(item.updated_at).toLocaleString("pt-BR")}
        </p>
      )}
      <form onSubmit={handleSave} className="space-y-2">
        <input
          type="password"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Cole a nova chave para atualizar"
          className="w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm"
          autoComplete="new-password"
        />
        <div className="flex flex-wrap gap-2">
          <button
            type="submit"
            disabled={saving || !value.trim()}
            className="btn-primary text-sm disabled:opacity-50"
          >
            {saving ? "Salvando..." : "Salvar"}
          </button>
          {item.source === "db" && (
            <button
              type="button"
              onClick={async () => {
                if (!confirm(`Remover ${item.label}? O fallback env, se houver, volta a ser usado.`)) return;
                try {
                  await onDelete();
                  setMsg("Removida.");
                } catch (err) {
                  setMsg((err as Error).message);
                }
              }}
              className="rounded-lg border border-destructive/60 px-3 py-2 text-sm text-destructive"
            >
              Remover
            </button>
          )}
        </div>
        {msg && <p className="text-xs text-muted-foreground">{msg}</p>}
      </form>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Cron externo tab
// ═══════════════════════════════════════════════════════════════

function pad2(n: number) {
  return n.toString().padStart(2, "0");
}

function fmtSaoPaulo(unix: number | null | undefined) {
  if (!unix) return "—";
  try {
    return new Date(unix * 1000).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" });
  } catch {
    return "—";
  }
}

export function CronExternoTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["admin-cron-schedule"], queryFn: () => getCronSchedule() });

  const [enabled, setEnabled] = useState(false);
  const [timezone, setTimezone] = useState("America/Sao_Paulo");
  const [times, setTimes] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!q.data) return;
    setEnabled(!!q.data.enabled);
    setTimezone(q.data.timezone || "America/Sao_Paulo");
    const hs = (q.data.hours ?? []).filter((h: number) => h >= 0 && h <= 23);
    const ms = (q.data.minutes ?? []).filter((m: number) => m >= 0 && m <= 59);
    const combos: string[] = [];
    for (const h of hs) for (const m of ms) combos.push(`${pad2(h)}:${pad2(m)}`);
    combos.sort();
    setTimes(combos.length ? [...new Set(combos)] : []);
  }, [q.data]);

  const parsed = useMemo(() => {
    const hs = new Set<number>();
    const ms = new Set<number>();
    let bad = false;
    for (const t of times) {
      const m = /^(\d{2}):(\d{2})$/.exec(t.trim());
      if (!m) { bad = true; continue; }
      const h = Number(m[1]);
      const min = Number(m[2]);
      if (h < 0 || h > 23 || min < 0 || min > 59) { bad = true; continue; }
      hs.add(h); ms.add(min);
    }
    return { hours: [...hs].sort((a, b) => a - b), minutes: [...ms].sort((a, b) => a - b), bad };
  }, [times]);

  const preview = useMemo(() => {
    const arr: string[] = [];
    for (const h of parsed.hours) for (const m of parsed.minutes) arr.push(`${pad2(h)}:${pad2(m)}`);
    arr.sort();
    return arr;
  }, [parsed]);

  const wanted = useMemo(() => new Set(times.map((t) => t.trim())), [times]);
  const extras = preview.filter((t) => !wanted.has(t));

  const save = useMutation({
    mutationFn: () =>
      updateCronSchedule({
        data: {
          enabled,
          timezone,
          hours: parsed.hours,
          minutes: parsed.minutes,
        } as any,
      }),
    onSuccess: () => {
      setSavedMsg("Cron atualizado.");
      qc.invalidateQueries({ queryKey: ["admin-cron-schedule"] });
    },
    onError: (e) => setError((e as Error).message),
  });

  const runNow = useMutation({
    mutationFn: (count?: number) => runScheduleNow({ data: count ? { count } : {} }),
    onSuccess: (res: any) => {
      const gen = res?.generated ?? 0;
      const errs = (res?.errors ?? []) as string[];
      setSavedMsg(
        `Execução concluída: ${gen} artigo(s) gerado(s)${errs.length ? ` · ${errs.length} erro(s): ${errs[0]}` : ""}.`,
      );
      qc.invalidateQueries({ queryKey: ["admin-cron-schedule"] });
    },
    onError: (e) => setError((e as Error).message),
  });

  const canSave = !parsed.bad && parsed.hours.length > 0 && parsed.minutes.length > 0;

  return (
    <div className="space-y-4">
      {q.isLoading && <p className="text-sm text-muted-foreground">Carregando...</p>}
      {q.error && (
        <div className="card-glass border border-destructive/40 text-sm text-destructive">
          {(q.error as Error).message}
        </div>
      )}
      {q.data && (
        <>
          <div className="card-glass">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-semibold">{q.data.title ?? "Job cron-job.org"}</p>
                <p className="mt-1 break-all font-mono text-xs text-muted-foreground">{q.data.url}</p>
              </div>
              <span className={`rounded-full px-2 py-0.5 text-xs ${q.data.enabled ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                {q.data.enabled ? "Ativo" : "Pausado"}
              </span>
            </div>
            <div className="mt-3 grid gap-2 text-xs text-muted-foreground md:grid-cols-3">
              <div><strong className="text-foreground">Timezone:</strong> {q.data.timezone}</div>
              <div><strong className="text-foreground">Última execução:</strong> {fmtSaoPaulo(q.data.lastExecution)}</div>
              <div><strong className="text-foreground">Próxima execução:</strong> {fmtSaoPaulo(q.data.nextExecution)}</div>
            </div>
            {q.data.lastStatus != null && (
              <p className="mt-2 text-xs text-muted-foreground">Último status HTTP: <span className="font-mono">{q.data.lastStatus}</span></p>
            )}
            <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-border/60 pt-3">
              <button
                type="button"
                disabled={runNow.isPending}
                onClick={() => { setError(null); setSavedMsg(null); runNow.mutate(undefined); }}
                className="btn-primary text-sm disabled:opacity-50"
              >
                {runNow.isPending ? "Rodando..." : "Rodar cron agora"}
              </button>
              <button
                type="button"
                disabled={runNow.isPending}
                onClick={() => { setError(null); setSavedMsg(null); runNow.mutate(1); }}
                className="rounded-lg border border-border px-3 py-2 text-sm disabled:opacity-50"
              >
                Gerar 1 artigo agora
              </button>
              <p className="text-xs text-muted-foreground">
                Dispara o mesmo fluxo do cron externo (ignora janela de horário). Útil para testes e para adiantar artigos.
              </p>
            </div>
          </div>

          <div className="card-glass space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold">Horários de execução</h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  cron-job.org combina toda hora com todo minuto informados. Prefira horários com o mesmo minuto
                  (ex: 09:00, 12:00, 18:00) ou a mesma hora para evitar execuções extras.
                </p>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                />
                Cron ativo
              </label>
            </div>

            <div className="space-y-2">
              {times.length === 0 && (
                <p className="text-sm text-muted-foreground">Nenhum horário definido.</p>
              )}
              {times.map((t, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="time"
                    value={t}
                    onChange={(e) => {
                      const next = [...times];
                      next[i] = e.target.value;
                      setTimes(next);
                    }}
                    className="rounded-lg border border-border bg-background/60 px-3 py-2 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setTimes(times.filter((_, idx) => idx !== i))}
                    className="text-xs text-destructive hover:underline"
                  >
                    Remover
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => setTimes([...times, "09:00"])}
                className="rounded-lg border border-border px-3 py-2 text-sm"
              >
                + Adicionar horário
              </button>
            </div>

            {parsed.bad && (
              <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                Formato inválido em algum horário. Use HH:MM.
              </div>
            )}

            {preview.length > 0 && (
              <div className="rounded-lg border border-border bg-secondary/30 p-3 text-xs">
                <p className="font-semibold text-foreground">Execuções resultantes ({preview.length}):</p>
                <p className="mt-1 font-mono text-muted-foreground">{preview.join("  ·  ")}</p>
              </div>
            )}

            {extras.length > 0 && (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-400">
                <p className="font-semibold">Atenção: horários extras serão gerados</p>
                <p className="mt-1">
                  A API combina todos os minutos com todas as horas. Estes horários NÃO estavam na sua lista,
                  mas serão executados: <span className="font-mono">{extras.join(", ")}</span>. Use horários
                  com o mesmo minuto (ou a mesma hora) para evitar isso.
                </p>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                disabled={!canSave || save.isPending}
                onClick={() => { setError(null); setSavedMsg(null); save.mutate(); }}
                className="btn-primary disabled:opacity-50"
              >
                {save.isPending ? "Salvando..." : "Salvar cron"}
              </button>
              {savedMsg && <span className="text-xs text-primary">{savedMsg}</span>}
              {error && <span className="text-xs text-destructive">{error}</span>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
